import { Component, OnInit, ViewChild } from '@angular/core';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { CaseFileCategory } from '@app/shared/models/case-file-category';
import {
  CaseDistributionJobUriBuilderParams,
  DistributionService
} from '@app/core/services/distribution.service';
import {
  DistributionJob,
  DistributionJobChannel,
  DistributionJobItem,
  DistributionJobItemType,
  DistributionJobState,
  DistributionJobStates,
  isInSignDistributionJob,
} from '@app/shared/models/distribution-doc-models';
import { BaseWithIdList } from '@app/shared/models/list';
import { DistributionEventData, DistributionWebSocketMessages, DistributionWebSocketService } from '@app/core/services/web-socket/distribution-web-socket.service';
import { CaseComponent } from '@app/modules/case/components/case-base/case.component';
import { CaseService } from '@app/core/services/case.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { DistributionJobListComponent } from '@app/shared/components/distribution-job-list/distribution-job-list.component';
import { DrFileMetaCategoryList } from '@app/shared/models/dr-file';
import { TranslationEntryObject } from '@app/shared/models/translatable';
import { TemplateService } from '@app/core/services/template.service';
import * as _ from 'lodash-es';
import { CaseParticipantUriBuilderParams, ParticipantsService } from '@app/core/services/participants.service';
import { Participant } from '@app/shared/models/participant';

@Component({
  templateUrl: './case-detailed-distribution.component.html',
  styleUrls: ['./case-detailed-distribution.component.scss']
})
export class CaseDetailedDistributionComponent extends CaseComponent
                                               implements OnInit {

  public distributionJobList: BaseWithIdList<DistributionJob>;

  public activeDistributionJob: DistributionJob;
  public allAssignables: DrFileMetaCategoryList;
  public unfilteredAllAssignables: DrFileMetaCategoryList;
  public participants: BaseWithIdList<Participant>;

  public documentTypes: TranslationEntryObject[];
  public selectedDocumentType: TranslationEntryObject;

  @ViewChild('distributionJobListComponent')
  public distributionJobListComponent: DistributionJobListComponent;

  get DistributionJobState() {
    return DistributionJobState;
  }

  get CaseFileCategory() {
    return CaseFileCategory;
  }

  constructor(
      protected componentServiceBundle: ComponentServiceBundle,
      protected caseService: CaseService,
      protected distributionService: DistributionService,
      protected distributionWebSocketService: DistributionWebSocketService,
      protected templateService: TemplateService,
      protected participantsService: ParticipantsService
  ) {
    super(componentServiceBundle, caseService);
    this._idPrefix = 'case_detailed_distribution.';
  }

  public ngOnInit() {
    super.ngOnInit();
    this.subscribeToDistributionWebSockets();

    this.templateService.getSMPDocumentTypes()
                        .subscribe(result => {
      const docTypes = result.map(entry => new TranslationEntryObject(entry));

      this.documentTypes = _.sortBy(docTypes, entry => {
         return this.translateService.instant(entry.actionListDisplayName);
      });
    });
  }

  protected whenOriginalFetched(): void {
    super.whenOriginalFetched();
    this.distributionService.query(this.generateCaseDistributionJobUriBuilderParams(),  { queryParams: { item_id: this.case.id, item_type: DistributionJobItemType.SMP_CASE } })
                            .subscribe((result) => {
      this.distributionJobList = result;

      if (this.distributionJobList.hasItems) {
        setTimeout(() => {
          this.distributionJobListComponent.selectDistributionJob(this.distributionJobList.items[0]);
        });
      }
    }, err => {
      this.handleError(err);
    });

    this.participantsService.query(new CaseParticipantUriBuilderParams(this.case.id))
                            .subscribe( (participants: BaseWithIdList<Participant>) => {
      this.participants = participants;
    });
  }

  public generateCaseDistributionJobUriBuilderParams(job?: DistributionJob): CaseDistributionJobUriBuilderParams {
    return new CaseDistributionJobUriBuilderParams(job && job.item.id || null, job);
  }

  public getDocumentCountForCategory(category: string): number {
    const fileMetaList = this.allAssignables && this.allAssignables.getFilesByCategory(category);
    return fileMetaList && fileMetaList.hasItems && fileMetaList.entries.length || 0;
  }

  public selectDocumentType(documentType): void {
    this.selectedDocumentType = this.selectedDocumentType === documentType ? null : documentType;
  }

  private subscribeToDistributionWebSockets(): void {
    this.subscriptions.push(this.distributionWebSocketService.distributionSockets
                                                             .subscribe((push: DistributionEventData) => {
      console.log(`%c${push.message} - ${push.event}`, this.logStyle);
      this.distributionJobListComponent.updateListAndActiveJob(push.data);

      if (push.message === DistributionWebSocketMessages.SUCCESS) {
        const distributionJobStatesMessageKey = (push.data.channel === DistributionJobChannel.PRINT ? marker('distribution_job_state.PRINT') :
                                                                                                      DistributionJobStates.getByKey(push.data.state).trans);
        this.showSimpleConfirmSnackbar(distributionJobStatesMessageKey);

        if (this.activeDistributionJob.isInSignSignNow) {
          if (push.event === DistributionWebSocketService.WebSocketEvents.SENT) {
            this.distributionJobListComponent.openInSignPage();
          } else if (push.event === DistributionWebSocketService.WebSocketEvents.SIGNED) {
            this.distributionJobListComponent.selectDistributionJob(this.activeDistributionJob, true);
          }
        }
      } else if (push.message === DistributionWebSocketMessages.ERROR) {
        if (push.data && push.data.item && push.data.item.documents) {
          push.data.item.documents.forEach(doc => {
            if (doc.state === 'ERROR') {
              const errMsg = `ERROR FOR DOCUMENT: ${doc.display_name} - ${doc.state_description || 'no info provided'}`;
              console.log(`%c${errMsg}`, this.logStyle);
            }
          });
        }
        this.showSimpleInfoDialog(DistributionJobStates.getByKey(push.data.state).trans, push.data.state_text || marker('common.label.error_has_occurred'), '33%');
      }
    }));
  }

  public detectChanges(): void {
    this.activeDistributionJob = this.distributionJobListComponent && this.distributionJobListComponent.activeDistributionJob;
    this.allAssignables = this.activeDistributionJob && this.distributionJobListComponent.allAssignables && this.distributionJobListComponent.allAssignables;
    this.unfilteredAllAssignables = this.activeDistributionJob && this.distributionJobListComponent.allAssignables && this.distributionJobListComponent.unfilteredAllAssignables;
  }

  public createNewDistribution(): void {
    const newJob = new DistributionJob();
    newJob.item = new DistributionJobItem({ type: DistributionJobItemType.SMP_CASE, id: this.case.id });
    newJob.state = DistributionJobState.INITIAL;

    this.distributionService.create(this.generateCaseDistributionJobUriBuilderParams(), newJob)
                            .subscribe((result: DistributionJob) => {
      this.distributionJobList.addItem(result, true);

      setTimeout(() => {
        this.distributionJobListComponent.selectDistributionJob(result, true);
      }, 0);
    }, err => {
      this.handleError(err);
    });
  }

}
